import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core"
import { Field, Form, Formik } from "formik"
import { CheckboxWithLabel } from "formik-material-ui"
import { useContext, useState } from "react"
import { useMutation } from "react-query"
import * as Yup from "yup"
import { resetCourseProgress } from "~/actions/courses"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const ResetCourseProgress = () => {
  const classes = useStyles()
  const { dialogs, closeDialog, openSnackBar } = useContext(AppContext)
  const thisDialog = dialogs?.["resetCourseProgress"] || {}
  const { open = false, data } = thisDialog
  const openErrorSnackbar = useOpenErrorSnackbar()
  const userData = data
  const resetProgressMutation = useMutation(resetCourseProgress)

  const schema = Yup.object().shape({
    reset: Yup.array().min(1, "Please select one property to reset"),
  })

  const [selectAllChecked, setCheckedValue] = useState(null)

  const handleClose = () => {
    closeDialog("resetCourseProgress", false)
    setCheckedValue(null)
  }

  const onSubmit = (values, actions) => {
    const data = {
      user_id: userData.user_id,
      course_id: userData.course_id,
      remove: {
        progress: values.reset.includes("progress"),
        knowledge_checks: values.reset.includes("knowledge_checks"),
        sentiment_questions: values.reset.includes("sentiment_questions"),
        notes: values.reset.includes("notes"),
        assessment: values.reset.includes("assessment"),
      },
    }
    resetProgressMutation.mutate(data, {
      onSuccess: (res) => {
        openSnackBar({ message: `${res.message}` })
        actions.setSubmitting(false)
        handleClose()
      },
      onError: (err) => {
        actions.setSubmitting(false)
        openErrorSnackbar(err, "Error Resetting Progress")
      },
    })
  }

  return (
    <Dialog maxWidth="xs" open={open}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Typography variant="h2" className={classes.title}>
          Reset Course Progress
        </Typography>
        <Typography color="primary" variant="subtitle2">
          {`Select the properties you would like to reset for ${userData?.first_name} ${userData?.last_name}`}
        </Typography>
      </DialogTitle>
      <Formik
        initialValues={{
          reset: [],
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({ isSubmitting, errors, setFieldValue, values }) => (
          <Form>
            <DialogContent>
              <Box>
                <Field
                  component={CheckboxWithLabel}
                  name="reset"
                  value="all"
                  Label={{ label: "Select All" }}
                  onChange={(e) => {
                    setCheckedValue(e.target.checked)
                    if (e.target.checked === true) {
                      setFieldValue("reset", [
                        "progress",
                        "knowledge_checks",
                        "sentiment_questions",
                        "notes",
                        "assessment",
                      ])
                    } else {
                      setFieldValue("reset", [])
                    }
                  }}
                />
              </Box>
              <Box>
                <Field
                  component={CheckboxWithLabel}
                  name="reset"
                  value="progress"
                  Label={{ label: "Course Progress" }}
                  checked={
                    selectAllChecked || values.reset.includes("progress")
                  }
                />
              </Box>
              <Box>
                <Field
                  component={CheckboxWithLabel}
                  name="reset"
                  value="knowledge_checks"
                  Label={{ label: "Knowledge Checks" }}
                  checked={
                    selectAllChecked ||
                    values.reset.includes("knowledge_checks")
                  }
                />
              </Box>
              <Box>
                <Field
                  component={CheckboxWithLabel}
                  name="reset"
                  value="sentiment_questions"
                  Label={{ label: "Sentiment Questions" }}
                  checked={
                    values.reset.includes("sentiment_questions") ||
                    selectAllChecked
                  }
                />
              </Box>
              <Box>
                <Field
                  component={CheckboxWithLabel}
                  name="reset"
                  value="notes"
                  Label={{ label: "Notes" }}
                  checked={values.reset.includes("notes") || selectAllChecked}
                />
              </Box>
              <Box>
                <Field
                  component={CheckboxWithLabel}
                  name="reset"
                  value="assessment"
                  Label={{ label: "Assessments" }}
                  checked={
                    values.reset.includes("assessment") || selectAllChecked
                  }
                />
              </Box>
              {errors.reset && (
                <Typography className={classes.requiredText}>
                  Please select at least one property to reset
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Box>
                <Button
                  onClick={handleClose}
                  style={{ height: "40px", marginRight: "8px" }}
                >
                  Cancel
                </Button>
                <LoaderButton
                  type="submit"
                  classoverrides={{
                    wrapper: classes.button,
                    button: {
                      root: classes.buttonRoot,
                    },
                  }}
                  variant="contained"
                  color="secondary"
                  working={isSubmitting}
                  size="small"
                >
                  Submit
                </LoaderButton>
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

ResetCourseProgress.propTypes = {}

export default ResetCourseProgress
